import { styled } from '@linaria/react'
import { font } from '@reapit/elements/utils/font'
import { Button } from '@reapit/elements/core/button'

/** Full-height shell so the builder owns its own scroll (it renders outside the app's PageLayout). */
export const BuilderShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`

/* ---- Shared builder-view chrome (split layout + left-hand structure tree) ----
   Used by both the Agent App and Recipient App builder views, so it lives here rather than in a
   single view's style file. */

/** Two-column builder view: sticky structure tree on the left, canvas on the right. */
export const BuilderSplitLayout = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 1.5rem;
  align-items: flex-start;
  margin-top: 1rem;

  @media screen and (max-width: 860px) {
    flex-direction: column;
    row-gap: 1rem;
  }
`

export const LeftColumn = styled.div`
  width: 20rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
  position: sticky;
  top: 1rem;

  @media screen and (max-width: 860px) {
    position: static;
    width: 100%;
  }
`

/** White rounded card matching the document view's tiles/content cards. */
export const CardTile = styled.div`
  background-color: white;
  border: 1px solid var(--neutral-100, #e5e9ed);
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(18, 43, 51, 0.06);
  padding: 1rem;
`

/** Left-panel structure outline: top-level items → nested items → contextual add actions. */
export const OutlineTree = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.875rem;
`

export const OutlineCard = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.25rem;
`

/** Tree-row top-level name (Button, styled to sit flush-left as a nav row rather than a standalone button). */
export const OutlineCardName = styled(Button)`
  ${font('sm', 'bold')}
  display: flex;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  padding: 0.125rem 0 !important;
  color: var(--colour-text-primary, #222b33) !important;

  &:hover {
    color: var(--colour-text-action, #a1244d) !important;
  }
`

export const OutlineSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.125rem;
  margin-left: 0.25rem;
  padding-left: 0.625rem;
  border-left: 1px solid var(--neutral-100, #e5e9ed);
`

/** Tree-row nested name (Button, styled to sit flush-left as a nav row rather than a standalone button). */
export const OutlineSectionName = styled(Button)`
  ${font('sm', 'regular')}
  display: flex;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  padding: 0.125rem 0 !important;
  color: var(--colour-text-primary, #222b33) !important;

  &:hover {
    color: var(--colour-text-action, #a1244d) !important;
  }
`

export const OutlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  padding: 0.125rem 0 0.25rem;
`

/** Compact "+ Add X" tree action (Button, sized down to fit the dense outline). */
export const OutlineAddLink = styled(Button)`
  ${font('xs', 'bold')}
  padding: 0 !important;
`

/** Larger add link for the top-level entry action (e.g. "+ Add Review Card" / "+ Add Step"). */
export const OutlineAddCardLink = styled(Button)`
  ${font('sm', 'bold')}
  padding: 0.25rem 0 !important;
  margin-bottom: 1.25rem;
`

export const OutlineSectionNote = styled.div`
  ${font('xs', 'regular')}
  color: var(--colour-text-secondary, #8c99a8);
  padding: 0.125rem 0;
`

/** Right-hand builder canvas column. */
export const Canvas = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`

/** Gray builder body (below the top bar); the scroll region, cards sit white on top. */
export const BuilderBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  background-color: var(--colour-fill-neutral-lightest, #f2f4f6);
`

export const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 1.5rem 1.5rem 0.5rem;
`

export const PageTitle = styled.h1`
  ${font('2xl', 'bold')}
  color: var(--colour-text-primary, #222b33);
  margin-bottom: 0.5rem;
`

export const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 0.25rem;
`

export const MetaItem = styled.span`
  display: inline-flex;
  flex-direction: row;
  align-items: baseline;
  column-gap: 0.375rem;
`

export const ContentInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 1rem 1.5rem 3rem;
`

export const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60vh;
`

export const SideCardHelp = styled.p`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  line-height: 1.5;
  margin: 0 0 0.875rem;
`

/** Sits above the tabs, explaining the left-to-right build order — the tabs themselves don't say why that
 * order matters (e.g. Recipient/PDF Mapping both need Agent App fields to exist before they can link to them).
 * Plain text, no card/box treatment — it's a hint, not a callout. */
export const BuildGuideText = styled.p`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  line-height: 1.5;
  margin: 0 0 1rem;
`

/** Wrapping row of field previews; each item's basis comes from the field's editWidth. Shared by every
 * component that renders a list of field previews (agent view, collections, factory sections). */
export const FieldPreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0.75rem 0 0;
`

/** Holds a section/card/collection Edit link, pushed to the right of its footer row. */
export const SectionEditRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
`

export const FieldPreviewLabel = styled.label`
  ${font('sm', 'medium')}
  color: var(--colour-text-secondary, #607890);
`

/** Light amber chip marking a field/collection as having a visibility rule; full rule shown on hover. */
export const FieldConditionBadge = styled.div`
  ${font('xs', 'regular')}
  align-self: flex-start;
  padding: 0.0625rem 0.5rem;
  border-radius: 1rem;
  border: 1px solid #f0c674;
  background-color: #fdf3e0;
  color: #8a5a00;
  cursor: default;
`

export const FieldPreviewRequired = styled.span`
  color: var(--colour-text-danger, #d4285a);
  margin-left: 0.125rem;
`

export const FieldPreviewControl = styled.input`
  ${font('base', 'regular')}
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  background-color: white;
  color: var(--colour-text-primary, #222b33);
  opacity: 1;
  -webkit-text-fill-color: var(--colour-text-primary, #222b33);
  cursor: default;

  &::placeholder {
    color: var(--colour-text-secondary, #8c99a8);
    -webkit-text-fill-color: var(--colour-text-secondary, #8c99a8);
  }
`

export const FieldPreviewTextArea = styled.textarea`
  ${font('base', 'regular')}
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  background-color: white;
  color: var(--colour-text-primary, #222b33);
  opacity: 1;
  -webkit-text-fill-color: var(--colour-text-primary, #222b33);
  resize: none;
  cursor: default;

  &::placeholder {
    color: var(--colour-text-secondary, #8c99a8);
    -webkit-text-fill-color: var(--colour-text-secondary, #8c99a8);
  }
`

/** Wraps a control that needs a leading symbol (e.g. currency $). */
export const FieldPreviewAdorned = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  background-color: white;
  overflow: hidden;
`

export const FieldPreviewAdornment = styled.span`
  ${font('base', 'regular')}
  display: inline-flex;
  align-items: center;
  padding: 0 0.625rem;
  color: var(--colour-text-secondary, #607890);
  background-color: var(--colour-fill-neutral-lightest, #f2f4f6);
  border-right: 1px solid var(--neutral-150, #d8dee4);
`

export const FieldPreviewAdornedInput = styled.input`
  ${font('base', 'regular')}
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  padding: 0.5rem 0.75rem;
  background-color: white;
  color: var(--colour-text-primary, #222b33);
  opacity: 1;
  -webkit-text-fill-color: var(--colour-text-primary, #222b33);
  cursor: default;

  &::placeholder {
    color: var(--colour-text-secondary, #8c99a8);
    -webkit-text-fill-color: var(--colour-text-secondary, #8c99a8);
  }
`

export const FieldPreviewHelp = styled.div`
  ${font('xs', 'regular')}
  color: var(--colour-text-secondary, #8c99a8);
`

/** Disabled select-look box for a factory/field dropdown preview. */
export const PreviewSelectBox = styled.div`
  ${font('base', 'regular')}
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--neutral-150, #d8dee4);
  border-radius: 0.375rem;
  background-color: white;
  color: var(--colour-text-secondary, #8c99a8);
`

/** Radio options laid out side by side (wrapping), matching the agent's inline radio group. */
export const PreviewRadioList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 1.5rem;
  row-gap: 0.5rem;
`

/** Checkbox options stacked vertically, one per row - unlike RadioList's side-by-side layout, a CheckboxGroup
 * is typically a longer list (e.g. grounds for entry) where wrapping into a horizontal flow reads poorly. */
export const PreviewCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
`

export const PreviewOptionRow = styled.div`
  ${font('base', 'regular')}
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  color: var(--colour-text-primary, #222b33);
`

export const PreviewRadioMark = styled.span`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 1px solid var(--neutral-200, #c3ccd5);
  flex-shrink: 0;
`

export const EmptyState = styled.div`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  background-color: white;
  border: 1px dashed var(--neutral-150, #d8dee4);
  border-radius: 0.75rem;
  padding: 2.5rem 2rem;
  text-align: center;
`

export const PlaceholderPanel = styled.div`
  ${font('sm', 'regular')}
  color: var(--colour-text-secondary, #607890);
  padding: 2.5rem 0;
  text-align: center;
`
